"use client";

import type { ReactNode } from "react";
import type { AddressSearchOutput } from "@tillix/api-contract/client-schemas";
import { Button } from "@tillix/ui-atoms/button";
import { Form } from "@tillix/ui-atoms/form";
import { FormRootError } from "@tillix/ui-atoms/form-root-error";
import {
  Tabs,
  TabsList,
  TabsPanel,
  TabsTab,
} from "@tillix/ui-atoms/framing/tabs";
import { observer } from "mobx-react-lite";
import { useT } from "@/features/i18n/hooks/use-t";
import AddressSearchForm from "./AddressSearchForm";

interface AccountLookupTabsProps {
  tenantId: string;
  /** Content rendered inside the "Account Number" tab (form fields) */
  renderAccountFields: () => ReactNode;
  /** Form submit handler for the account number tab */
  onAccountSubmit: React.FormEventHandler<HTMLFormElement>;
  /** Submit button label */
  submitLabel: string;
  /** Submit button label while submitting */
  submittingLabel: string;
  /** Whether the form is currently submitting */
  isSubmitting: boolean;
  /** Whether the form is valid */
  isValid: boolean;
  /** Optional extra disable condition (e.g. quick-pay disables after lookup) */
  isSubmitDisabled?: boolean;
  /** Root form error message */
  rootError?: string;
  /** Prefix for the FormRootError key */
  errorKeyPrefix: string;
  /** Callback when address search returns results */
  onSearchResults: (results: AddressSearchOutput["results"]) => void;
  /** Whether tabs are visible. When false, only the account number form renders */
  showTabs: boolean;
  /** Optional slot for rendering address search results */
  renderAddressResults?: () => ReactNode;
  /** Controlled tab value */
  tabValue?: string | null;
  /** Callback when tab changes */
  onTabChange?: (value: string | null) => void;
}

function AccountLookupTabs({
  tenantId,
  renderAccountFields,
  onAccountSubmit,
  submitLabel,
  submittingLabel,
  isSubmitting,
  isValid,
  isSubmitDisabled,
  rootError,
  errorKeyPrefix,
  onSearchResults,
  showTabs,
  renderAddressResults,
  tabValue,
  onTabChange,
}: AccountLookupTabsProps) {
  const { t } = useT();
  const accountForm = (
    <Form className="flex flex-col gap-0" onSubmit={onAccountSubmit}>
      {renderAccountFields()}
      <FormRootError keyPrefix={errorKeyPrefix} rootError={rootError} />
      <Button
        className="mt-2 w-full"
        disabled={isSubmitting || !isValid || isSubmitDisabled}
        type="submit"
      >
        {isSubmitting ? submittingLabel : submitLabel}
      </Button>
    </Form>
  );

  if (!showTabs) {
    return accountForm;
  }

  return (
    <Tabs
      onValueChange={onTabChange}
      value={tabValue}
      {...(!(tabValue || onTabChange) ? { defaultValue: "account" } : {})}
    >
      <TabsList className="mb-4 w-full">
        <TabsTab className="flex-1" value="account">
          {t("quickPay.accountNumber")}
        </TabsTab>
        <TabsTab className="flex-1" value="address">
          {t("quickPay.address")}
        </TabsTab>
      </TabsList>

      <TabsPanel value="account">{accountForm}</TabsPanel>

      <TabsPanel value="address">
        <AddressSearchForm
          onSearchResults={onSearchResults}
          tenantId={tenantId}
        />
        {renderAddressResults?.()}
      </TabsPanel>
    </Tabs>
  );
}

export type { AccountLookupTabsProps };
export default observer(AccountLookupTabs);
